import { useState, ChangeEvent, useEffect, useRef } from 'react';
import { format, parse, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatCPF, formatPhone, formatCurrency, formatCEP, formatCpfCnpj } from '@/utils/format';

export function useFormFormatters(initialDates?: Record<string, Date>) {
  const [dateInputs, setDateInputs] = useState<Record<string, string>>({});
  const initialDatesRef = useRef(initialDates);

  // Inicializa os inputs de data com os valores existentes
  useEffect(() => {
    if (initialDatesRef.current && Object.keys(dateInputs).length === 0) {
      const formattedDates = Object.entries(initialDatesRef.current).reduce((acc, [key, value]) => {
        if (value && isValid(value)) {
          acc[key] = format(value, 'dd/MM/yyyy', { locale: ptBR });
        }
        return acc;
      }, {} as Record<string, string>);
      
      setDateInputs(formattedDates);
    }
  }, []); // Executar apenas na montagem

  const formatData = (value: string): string => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue.length <= 2) return cleanValue;
    if (cleanValue.length <= 4) return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2)}`;
    return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}/${cleanValue.slice(4, 8)}`;
  };

  const stringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const parsedDate = parse(dateString, 'dd/MM/yyyy', new Date());
    return isValid(parsedDate) ? parsedDate : null;
  };

  const handleDateInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldOnChange: (date: Date | null) => void,
    fieldName: string
  ) => {
    const value = e.target.value;
    const formattedValue = formatData(value);
    setDateInputs(prev => ({ ...prev, [fieldName]: formattedValue }));

    if (formattedValue.length === 10) {
      const date = stringToDate(formattedValue);
      fieldOnChange(date);
    }
  };

  const handlePopoverOpenChange = (
    open: boolean,
    value: Date | undefined,
    fieldName: string
  ) => {
    if (!open && value && isValid(value)) {
      const formattedValue = format(value, 'dd/MM/yyyy', { locale: ptBR });
      setDateInputs(prev => ({
        ...prev,
        [fieldName]: formattedValue
      }));
    }
  };

  const handleDateSelect = (
    date: Date | undefined,
    fieldOnChange: (date: Date | null) => void,
    fieldName: string
  ) => {
    if (date) {
      fieldOnChange(date);
      const formattedValue = format(date, 'dd/MM/yyyy', { locale: ptBR });
      setDateInputs(prev => ({ ...prev, [fieldName]: formattedValue }));
    }
  };

  const cleanNumber = (value: string): string => {
    return value.replace(/\D/g, "");
  };

  return {
    formatCPF,
    formatCpfCnpj,
    formatTelefone: formatPhone,
    formatMoeda: formatCurrency,
    formatData,
    formatCEP,
    stringToDate,
    handleDateInputChange,
    handleDateSelect,
    handlePopoverOpenChange,
    cleanNumber,
    dateInputs,
  };
} 